import * as THREE from "three";
import { CSS2DRenderer } from "three/addons/renderers/CSS2DRenderer.js";
import { PLANET_RADIUS, mulberry32 } from "./sphere";

export function createRenderer(container: HTMLElement): {
  renderer: THREE.WebGLRenderer;
  labels: CSS2DRenderer;
} {
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: "high-performance" });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(container.clientWidth, container.clientHeight);
  renderer.setClearColor(0x07060b, 1);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.domElement.setAttribute("aria-label", "量化微缩星球场景");
  container.append(renderer.domElement);

  const labels = new CSS2DRenderer();
  labels.setSize(container.clientWidth, container.clientHeight);
  labels.domElement.style.position = "absolute";
  labels.domElement.style.inset = "0";
  labels.domElement.style.pointerEvents = "none";
  container.append(labels.domElement);

  return { renderer, labels };
}

export function createLights(scene: THREE.Scene): void {
  const hemi = new THREE.HemisphereLight(0x1c1830, 0x0a080c, 0.7);
  scene.add(hemi);

  const sun = new THREE.DirectionalLight(0xe8dcc8, 1.35);
  sun.position.set(18, 22, 10);
  scene.add(sun);

  const fill = new THREE.DirectionalLight(0x3a2a55, 0.28);
  fill.position.set(-16, -6, -12);
  scene.add(fill);

  scene.add(new THREE.AmbientLight(0x141018, 0.22));
}

export function createStarfield(): THREE.Points {
  const count = 1800;
  const positions = new Float32Array(count * 3);
  const rng = mulberry32(0x91c3);
  for (let i = 0; i < count; i += 1) {
    const r = 80 + rng() * 140;
    const phi = Math.acos(2 * rng() - 1);
    const theta = rng() * Math.PI * 2;
    const i3 = i * 3;
    positions[i3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i3 + 1] = r * Math.cos(phi);
    positions[i3 + 2] = r * Math.sin(phi) * Math.sin(theta);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const mat = new THREE.PointsMaterial({
    color: 0xd8d0c4,
    size: 0.35,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
  });
  const points = new THREE.Points(geo, mat);
  points.name = "stars";
  return points;
}

export function createPlanet(): THREE.Group {
  const group = new THREE.Group();
  const map = makePlanetTexture();
  const globe = new THREE.Mesh(
    new THREE.SphereGeometry(PLANET_RADIUS, 96, 64),
    new THREE.MeshStandardMaterial({
      map,
      color: 0xffffff,
      roughness: 0.86,
      metalness: 0.08,
    }),
  );
  globe.name = "globe";
  group.add(globe);

  const atmosphere = new THREE.Mesh(
    new THREE.SphereGeometry(PLANET_RADIUS * 1.045, 48, 32),
    new THREE.MeshBasicMaterial({
      color: 0x6b5b8c,
      transparent: true,
      opacity: 0.16,
      side: THREE.BackSide,
      depthWrite: false,
    }),
  );
  group.add(atmosphere);

  const equator = new THREE.Mesh(
    new THREE.TorusGeometry(PLANET_RADIUS + 0.03, 0.018, 8, 160),
    new THREE.MeshBasicMaterial({ color: 0x8a7344, transparent: true, opacity: 0.38 }),
  );
  equator.rotation.x = Math.PI / 2;
  group.add(equator);

  const meridian = new THREE.Mesh(
    new THREE.TorusGeometry(PLANET_RADIUS + 0.03, 0.014, 8, 160),
    new THREE.MeshBasicMaterial({ color: 0x6e5f88, transparent: true, opacity: 0.28 }),
  );
  group.add(meridian);

  return group;
}

function makePlanetTexture(): THREE.CanvasTexture {
  const w = 1024;
  const h = 512;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("2d context unavailable");
  }
  const rng = mulberry32(0x51a2);
  ctx.fillStyle = "#121018";
  ctx.fillRect(0, 0, w, h);

  for (let i = 0; i < 48; i += 1) {
    const x = rng() * w;
    const y = 36 + rng() * (h - 72);
    ctx.fillStyle = `rgba(${28 + rng() * 36},${26 + rng() * 28},${46 + rng() * 42},${0.28 + rng() * 0.4})`;
    ctx.beginPath();
    ctx.ellipse(x, y, 36 + rng() * 170, 16 + rng() * 64, rng() * Math.PI, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.strokeStyle = "rgba(168, 148, 98, 0.16)";
  ctx.lineWidth = 1;
  for (let i = 0; i <= 12; i += 1) {
    const y = (i / 12) * h;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }
  for (let i = 0; i <= 24; i += 1) {
    const x = (i / 24) * w;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
    ctx.stroke();
  }

  const eq = h / 2;
  ctx.strokeStyle = "rgba(106, 143, 122, 0.55)";
  ctx.lineWidth = 2;
  for (let i = 0; i < 36; i += 1) {
    const x = 40 + i * 27;
    const bull = rng() > 0.46;
    const hgt = 6 + rng() * 18;
    ctx.strokeStyle = bull ? "rgba(106, 143, 122, 0.5)" : "rgba(165, 107, 92, 0.5)";
    ctx.beginPath();
    ctx.moveTo(x, eq);
    ctx.lineTo(x, eq + (bull ? -hgt : hgt));
    ctx.stroke();
  }

  ctx.fillStyle = "rgba(196, 163, 90, 0.55)";
  for (let i = 0; i < 90; i += 1) {
    ctx.fillRect(rng() * w, rng() * h, 1.2, 1.2);
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  tex.needsUpdate = true;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}
